import {
  CURRENCY_VS_SYMBOL_MAP,
  SerializedStakeholder,
  StakeholderRelationshipTypeMap,
  fetcher,
  formatNumberWithUniqueCommaPattern,
} from '@commenda/utils';
import { TrashIcon } from '@heroicons/react/20/solid';
import { Currency } from '@prisma/client';
import { Country as CountryLib } from 'country-state-city';
import useModal from '../../../hooks/useModal';
import ConfirmDeleteModal from '../../modal/confirm-delete-modal';

const DeleteSection = ({
  id,
  token,
  mutate,
  onDeleteSuccess,
  onDeleteFailure,
}: {
  id: string;
  token: string;
  mutate: () => void;
  onDeleteSuccess: () => void;
  onDeleteFailure: () => void;
}) => {
  const onDeleteStakeholder = async () => {
    const response = await fetcher(
      `capTable/stakeholding/${id}`,
      { method: 'DELETE' },
      token
    );

    if (!response.ok) {
      onDeleteFailure();
      return false;
    }

    onDeleteSuccess();

    await mutate();

    return true;
  };

  const {
    isShown: isDeleteStakeholderModalShown,
    toggle: toggleDeleteStakeholderModalVisibility,
  } = useModal();

  return (
    <div>
      <ConfirmDeleteModal
        isDeleteModalShowed={isDeleteStakeholderModalShown}
        toggleDeleteModal={toggleDeleteStakeholderModalVisibility}
        headerText="Really delete this stakeholder?"
        modalMessage="This action cannot be reversed."
        onDelete={onDeleteStakeholder}
      />
      <TrashIcon
        className="h-6 w-6 text-gray-400 hover:text-red-500 cursor-pointer"
        aria-hidden="true"
        onClick={toggleDeleteStakeholderModalVisibility}
      />
    </div>
  );
};

const StakeholdersTable = ({
  stakeholders,
  outStandingShares,
  currency,
  onDeleteSuccess,
  onDeleteFailure,
  token,
  mutate,
}: {
  stakeholders: SerializedStakeholder[];
  outStandingShares: number;
  currency: Currency;
  token: string;
  mutate: () => void;
  onDeleteSuccess: () => void;
  onDeleteFailure: () => void;
}) => {
  return (
    <div className="flex flex-col gap-4">
      <div className="mt-6 flow-root w-full">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Email Address
                    </th>

                    <th
                      scope="col"
                      className="py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Relationship Type
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Type
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Nationality
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Shares
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Amount
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {stakeholders?.map((stakeholder) => {
                    return (
                      <tr key={stakeholder?.email}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {stakeholder?.name}
                        </td>
                        <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
                          {stakeholder?.email}
                        </td>
                        <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
                          {
                            StakeholderRelationshipTypeMap[
                              stakeholder?.relationshipType
                            ]
                          }
                        </td>
                        <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
                          {stakeholder?.type === 'INDIVIDUAL'
                            ? 'Individual'
                            : stakeholder?.type === 'INSTITUTION'
                              ? 'Entity'
                              : null}
                        </td>
                        <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
                          {
                            CountryLib.getCountryByCode(
                              stakeholder?.nationality
                            )?.name
                          }
                        </td>
                        <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
                          {
                            // TODO: replace with INTL library:
                            formatNumberWithUniqueCommaPattern(
                              stakeholder?.numberOfShares
                            )
                          }
                        </td>
                        <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
                          {CURRENCY_VS_SYMBOL_MAP[currency]}
                          {formatNumberWithUniqueCommaPattern(
                            stakeholder?.amount,
                            2
                          )}
                        </td>
                        <td className="pr-8 cursor-pointer hover:text-commenda-blue text-blue-40 font-medium transition">
                          <DeleteSection
                            id={stakeholder?.id}
                            token={token}
                            mutate={mutate}
                            onDeleteSuccess={onDeleteSuccess}
                            onDeleteFailure={onDeleteFailure}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StakeholdersTable;
